import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import {
  Card, CardHeader, CardBody, PageHeader,
  TableWrapper, Table, Th, Td, Badge
} from '../../components/ui';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';

const empty = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: 0,
  maxRedemptions: '',
  expiresAt: '',
};

export default function ManageCoupons() {
  const modal = useModal();
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const res = await apiGet('/api/coupons');
    setCoupons(res?.coupons || res?.data || []);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      code: form.code,
      description: form.description,
      discountType: form.discountType,
      discountValue: Number(form.discountValue) || 0,
      maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
      expiresAt: form.expiresAt ? new Date(form.expiresAt) : null,
    };

    let res;
    try {
      if (editingId) res = await apiPut(`/api/coupons/${editingId}`, payload);
      else res = await apiPost('/api/coupons', payload);

      if (res?.success) {
        setForm(empty);
        setEditingId(null);
        load();
      } else {
        modal.alert(res?.message || 'Failed', 'error');
      }
    } catch (err) {
      modal.alert(err?.message || 'Failed to save coupon', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    if (!(await modal.confirm('Delete this coupon?', { danger: true, confirmText: "Delete" }))) return;
    const res = await apiDelete(`/api/coupons/${id}`);
    if (res?.success) load();
  };

  const edit = (c) => {
    setEditingId(c.id);
    setForm({
      code: c.code || '',
      description: c.description || '',
      discountType: c.discountType || 'percent',
      discountValue: c.discountValue || 0,
      maxRedemptions: c.maxRedemptions || '',
      expiresAt: c.expiresAt ? new Date(c.expiresAt).toISOString().slice(0, 10) : '',
    });
  };

  return (
    <div>
      <PageHeader title="Manage Coupons" subtitle="Create and manage discount coupons" />

      <Card className="mb-6">
        <CardHeader>{editingId ? 'Edit Coupon' : 'Create Coupon'}</CardHeader>
        <CardBody>
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div className="input-group">
                <label className="input-label">Code</label>
                <input className="input" placeholder="SUMMER20" value={form.code} onChange={e => setForm({...form, code: e.target.value})} required />
              </div>
              <div className="input-group">
                <label className="input-label">Discount Type</label>
                <select className="input" value={form.discountType} onChange={e => setForm({...form, discountType: e.target.value})}>
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              <div className="input-group">
                <label className="input-label">
                  Discount Value {form.discountType === 'percent' ? '(%)' : '($)'}
                </label>
                <input className="input" type="number" placeholder={form.discountType === 'percent' ? '20' : '5.00'} value={form.discountValue} onChange={e => setForm({...form, discountValue: e.target.value})} required />
              </div>
              <div className="input-group">
                <label className="input-label">Description</label>
                <input className="input" placeholder="Summer sale discount" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div className="input-group">
                <label className="input-label">Max Redemptions</label>
                <input className="input" type="number" placeholder="Unlimited" value={form.maxRedemptions} onChange={e => setForm({...form, maxRedemptions: e.target.value})} />
                <p className="input-hint">Leave empty for unlimited</p>
              </div>
              <div className="input-group">
                <label className="input-label">Expiry Date</label>
                <input className="input" type="date" value={form.expiresAt} onChange={e => setForm({...form, expiresAt: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : editingId ? 'Update Coupon' : 'Create Coupon'}
              </button>
              {editingId && (
                <button type="button" className="btn-secondary" onClick={() => { setEditingId(null); setForm(empty); }}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>All Coupons</CardHeader>
        <CardBody className="p-0">
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Type</Th>
                  <Th>Discount</Th>
                  <Th>Redemptions</Th>
                  <Th>Expiry</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <tr key={c.id}>
                    <Td><code className="font-mono text-sm">{c.code}</code></Td>
                    <Td className="capitalize">{c.discountType || 'percent'}</Td>
                    <Td>
                      {c.discountType === 'fixed' ? `$${c.discountValue}` : `${c.discountValue}%`}
                    </Td>
                    <Td>
                      {c.redeemedCount || 0}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ' / \u221e'}
                    </Td>
                    <Td>{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '-'}</Td>
                    <Td>
                      <Badge variant={c.active !== false ? 'success' : 'danger'}>
                        {c.active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        <button className="btn-secondary btn-sm" onClick={() => edit(c)}>
                          <FaEdit size={12} />
                        </button>
                        <button className="btn-danger btn-sm" onClick={() => del(c.id)}>
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {coupons.length === 0 && (
                  <tr><Td colSpan="7" className="text-center text-ink-muted py-6">No coupons yet</Td></tr>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </CardBody>
      </Card>
    </div>
  );
}
